import { authApi, authProblem, isRevokedDevice, sessionFromMe } from '@/entities/auth';
import {
    detectPlatform,
    isFieldPlatform,
    retireToken,
    useSessionStore,
    type Session,
} from '@/shared/api';

/**
 * Wat er met een sessie gebeurt na het inloggen: herstellen bij het opstarten,
 * verlengen zolang de app in gebruik is, wisselen van omgeving en uitloggen.
 *
 * De schermen gebruiken de hooks (`useLogout`, `useTenantSwitch`); de schil
 * roept `restoreSession` en `keepSessionFresh` aan.
 */

/**
 * Bij het opstarten: de bewaarde sessie is er al (offline-first), maar rollen,
 * omgevingen en huisstijl kunnen intussen veranderd zijn. `GET /auth/me` werkt
 * ze bij. Geen verbinding: dan werkt de app door met wat er bewaard was. Een
 * 401 beëindigt de sessie al in de http-client.
 */
export async function restoreSession(): Promise<Session | null> {
    const session = useSessionStore.getState().session;

    if (!session) {
        return null;
    }

    try {
        const me = await authApi.me(session);

        if (useSessionStore.getState().session?.token !== session.token) {
            /* Intussen ingelogd, gewisseld of uitgelogd: dit antwoord is oud. */
            return useSessionStore.getState().session;
        }

        const restored = sessionFromMe(me, session);

        await useSessionStore.getState().signIn(restored);

        return restored;
    } catch {
        return useSessionStore.getState().session;
    }
}

const HOUR = 60 * 60 * 1_000;

/**
 * Wanneer verlengen we? Een computer krijgt 12 uur en wordt verlengd zolang hij
 * gebruikt wordt; een tablet krijgt 14 dagen en wordt verlengd als hij online
 * komt en minder dan een week over heeft.
 */
export function shouldRefresh(
    session: Pick<Session, 'expiresAt'>,
    now: number = Date.now(),
    field: boolean = isFieldPlatform(detectPlatform()),
): boolean {
    if (!session.expiresAt) {
        return false;
    }

    const left = Date.parse(session.expiresAt) - now;

    return left > 0 && left < (field ? 7 * 24 * HOUR : 6 * HOUR);
}

let refreshing: Promise<void> | null = null;

/** Verlengt het token als dat nodig is. Nooit twee tegelijk. */
export function keepSessionFresh(now: number = Date.now()): Promise<void> {
    refreshing ??= refresh(now).finally(() => {
        refreshing = null;
    });

    return refreshing;
}

async function refresh(now: number): Promise<void> {
    const session = useSessionStore.getState().session;

    if (!session || useSessionStore.getState().impersonation || !shouldRefresh(session, now)) {
        return;
    }

    const restore = retireToken(session.token);

    try {
        await useSessionStore.getState().signIn(await authApi.refresh(session));
    } catch (error) {
        restore();
        await endRefusedSession(error);
    }
}

/**
 * Een geweigerd token beëindigt de sessie. Alleen een **ingetrokken** apparaat
 * eindigt als `device-unavailable` - het inlogscherm legt dan uit dat opnieuw
 * inloggen hier niet helpt tot het weer is toegelaten. Een apparaat dat te lang
 * niet bevestigd was of onbekend is, eindigt als `expired`: opnieuw inloggen werkt.
 */
async function endRefusedSession(error: unknown): Promise<void> {
    const problem = authProblem(error);

    if (problem === 'unauthenticated' || problem === 'device-unavailable') {
        await useSessionStore
            .getState()
            .signOut(isRevokedDevice(error) ? 'device-unavailable' : 'expired');
    }
}

/**
 * Naar een andere omgeving: een nieuw token, en het oude vervalt meteen. Mislukt
 * het, dan blijft de huidige sessie staan en gooit dit de fout door - het
 * scherm laat hem zien, er is geen stille terugval.
 */
export async function switchTenant(
    tenantUuid: string,
    options: { remember?: boolean } = {},
): Promise<Session> {
    const session = useSessionStore.getState().session;

    if (!session) {
        throw new Error('Er is geen sessie om mee te wisselen.');
    }

    const restore = retireToken(session.token);

    try {
        const next = await authApi.switchTenant(session, tenantUuid, options);

        await useSessionStore.getState().signIn(next);

        return next;
    } catch (error) {
        restore();
        await endRefusedSession(error);

        throw error;
    }
}

/**
 * Uitloggen: op dit apparaat meteen, het token intrekken op de achtergrond.
 * Zonder verbinding lukt dat laatste niet; dan verloopt het token vanzelf.
 */
export async function logout(): Promise<void> {
    const session = useSessionStore.getState().session;

    if (session) {
        void authApi.logout(session).catch(() => undefined);
    }

    await useSessionStore.getState().signOut('signed-out');
}
